import exportRuleQuery from '@/api/queries/exportRule';
import PageTemplate from '@/components/PageTemplate';
import docsMutation from '@/api/mutitations/docs';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { queryStringToObject } from '@/helpers/string';
import agentQuery from '@/api/queries/agent';
import ModalEmailExport from '@/components/Table/Export/EmailModal/index';
import { useDisclosure } from '@mantine/hooks';
import agentMutations from '@/api/mutitations/agent';
import processDataMutations from '@/api/mutitations/processData';
import prepareTranslate from '@/helpers/dictionary';
import { tablesForMonthlyPaymentKeys } from '@/helpers/enums';
import { downloadFile } from '@/helpers/download';
import { buildExportData } from './Monthlypayment.funtions';
import MonthlyPaymentTables from './tables';
import Footer from './footer';

const ViewMonthlyPayment = () => {
  const t = prepareTranslate();

  const navigate = useNavigate();
  const { query: rawUrlQuery, id } = useParams();
  const jsonQuery = queryStringToObject(rawUrlQuery);
  const { agent: agentId, year, month } = jsonQuery;

  const { mutateAsync: exportFileMutate } = docsMutation.useExportFile();
  const { mutateAsync: updateAgent } = agentMutations.useUpdateAgentItemBulk();
  const { mutateAsync: updateProcessData } = processDataMutations.useUpdateProcessDataBulk();
  const { mutateAsync: fetchAgents } = agentMutations.useGetAgents();
  const { data: exportRuleData } = exportRuleQuery.useGetAll();
  const { data: agentData } = agentQuery.useGetAgentById({ id: agentId?.[0] });
  const isAllAgents = !agentId || agentId.length === 0;

  const [tableData, setTablesData] = useState({});
  const [totalAmount, setTotalAmount] = useState(0);
  const [rowsToPay, setRowsToPay] = useState({});
  // const [paymentDate, setPaymentDate] = useState('');
  // const [dateError, setDateError] = useState('');
  const [selectedRowsByTable, setSelectedRowsByTable] = useState({});
  const [files, setFiles] = useState([]);
  const [opened, { open: openEmailModal, close: closeEmailModal }] = useDisclosure(false);

  const rulesForMonthlyPayment = exportRuleData
    ?.filter((item) => tablesForMonthlyPaymentKeys.includes(item.key))
    // eslint-disable-next-line max-len
    ?.sort((a, b) => tablesForMonthlyPaymentKeys.indexOf(a.key) - tablesForMonthlyPaymentKeys.indexOf(b.key));

  // Calculate expected table keys count
  const expectedTableKeys = rulesForMonthlyPayment?.reduce((count, rule) => {
    if (rule.variables && Array.isArray(rule.variables) && rule.variables.length > 0) {
      return count + rule.variables.length;
    }
    return count + 1;
  }, 0) || 0;

  // Check if all tables have loaded their data
  const loadedTablesCount = Object.values(tableData).filter(
    (table) => table && (table.data !== undefined || table.error),
  ).length;

  const isDataLoading = expectedTableKeys > 0 && loadedTablesCount < expectedTableKeys;

  const verifySendEmailProps = {
    title: t('pages.monthlyPayment.verifySendingEmail'),
    verifySendingEmail: true,
    message: t('pages.monthlyPayment.verifySendingEmailMessage'),
  };

  const emailData = {
    ...agentData?.personalDetails, date: month[0], year, sum: totalAmount,
  };

  const exportFileTitles = {
    commissions: t('pages.monthlyPayment.exportFileTitles.commissions', [agentData?.personalDetails?.fullName || '', month[0], year[0]]),
    expenses: t('pages.monthlyPayment.exportFileTitles.expenses', [agentData?.personalDetails?.fullName || '', month[0], year[0]]),
    additionalFees: t('pages.monthlyPayment.exportFileTitles.additionalFees', [agentData?.personalDetails?.fullName || '', month[0], year[0]]),
    monthlyPayment: t('pages.monthlyPayment.exportFileTitles.monthlyPayment', [agentData?.personalDetails?.fullName || '', month[0], year[0]]),
  };

  useEffect(() => {
    if (!rulesForMonthlyPayment || Object.keys(tableData).length > 0) return;

    const initialData = {};
    rulesForMonthlyPayment?.forEach((rule) => {
      // Check if this rule has multiple variables
      if (rule.variables && Array.isArray(rule.variables) && rule.variables.length > 0) {
        // Create a key for each variable
        rule.variables.forEach((variable) => {
          const tableKey = `${rule._id}-${variable._id}`;
          initialData[tableKey] = {};
        });
      } else {
        // Single table for this rule
        initialData[rule._id] = {};
      }
    });

    setTablesData(initialData);
  }, [rulesForMonthlyPayment, tableData]);

  useEffect(() => {
    const total = Object.values(rowsToPay)
      .reduce((sum, value) => sum + Number(value || 0), 0)
      .toFixed(2);
    setTotalAmount(total);
  }, [rowsToPay]);

  const generateExportFile = async (obj) => {
    const {
      ruleId, rows, option = 'excel', query = {}, dataOverride, tableKey, variableName, columns,
      visiblecolumnsTransforms,
    } = obj;

    const dataForBuildExportData = {
      ruleId,
      exportRuleData,
      exportFileTitles,
      tableData: tableKey ? { [ruleId]: tableData[tableKey] } : tableData,
      fileType: option,
      query,
      overrideColumns: dataOverride?.columns,
      overrideData: dataOverride?.data,
      rows,
      variableName,
      columns,
      visiblecolumnsTransforms,
    };

    const { exportData, fileTitle } = buildExportData(dataForBuildExportData);

    const fileData = await exportFileMutate(exportData);
    return { fileData, fileTitle };
  };

  const updateRowsStatus = async () => {
    const allSelectedRows = Object.entries(selectedRowsByTable)
      // eslint-disable-next-line no-unused-vars
      .filter(([_, rows]) => rows && rows.length > 0)
      .flatMap(([tableKey, rows]) => {
        // Extract ruleId from tableKey (could be "ruleId" or "ruleId-companyId")
        const ruleId = tableKey.includes('-') ? tableKey.split('-')[0] : tableKey;

        return rows.map((row) => ({
          ...row,
          ruleId,
        }));
      });

    try {
      const bulkUpdates = {
        processData: [],
        agent: [],
      };

      allSelectedRows.forEach((row) => {
        const {
          ruleId, row: rownumber, documentId, headerId,
        } = row;
        const currentRule = rulesForMonthlyPayment.find((rule) => rule._id === ruleId);

        if (currentRule?.service === 'agentCommission') {
          // headerId targets the nested cells path on process_datas
          // (cells.<headerId>.components.$). The agentCommission service
          // emits it per row (see equity-server splitToRowsHelper).
          bulkUpdates.processData.push({
            _id: documentId,
            row: rownumber,
            headerId,
            isPaid: true,
            payDate: new Date().toISOString(),
          });
        } else {
          const key = currentRule?.getDataParams?.key || '';
          bulkUpdates.agent.push({
            agentId: agentId?.[0],
            itemId: row.id || row._id,
            key,
            data: {
              isPaid: true,
              payDate: new Date().toISOString(),
            },
          });
        }
      });

      if (bulkUpdates.processData.length > 0) {
        await updateProcessData(bulkUpdates.processData);
      }

      if (bulkUpdates.agent.length > 0) {
        await updateAgent(bulkUpdates.agent);
      }
    } catch (error) {
      // TO DO SOMETHING
    }
  };

  const getTotalsTable = () => Object.entries(selectedRowsByTable)
    // eslint-disable-next-line no-unused-vars
    .filter(([_, rows]) => Array.isArray(rows) && rows.length > 0)
    .map(([tableId, rows]) => {
      const [ruleId, variableId] = tableId.split('-');
      const rule = exportRuleData.find((r) => r._id === ruleId);
      const isNegativeAmount = rule?.isNegativeAmount || false;

      const name = rule?.variables?.length
        ? (rule.variables.find((v) => String(v._id) === String(variableId))?.name || rule?.title)
        : rule?.title;

      return {
        name, ruleId, isNegativeAmount, rows,
      };
    });

  const buildSummeryTable = async () => {
    const totalsTable = getTotalsTable();

    if (isAllAgents) {
      // totalsTable = Object.entries(tableData)
      //   .filter(([, table]) => table && table.data
      //     && Array.isArray(table.data) && table.data.length > 0)
      //   .map(([tableId, table]) => {
      //     const [ruleId, variableId] = tableId.split('-');
      //     const rule = exportRuleData.find((r) => r._id === ruleId);
      //     const isNegativeAmount = rule?.isNegativeAmount || false;

      //     const variableName = rule?.variables?.find(
      //       (v) => String(v._id) === String(variableId),
      //     )?.name;
      //     const name = rule?.variables?.length
      //       ? (variableName || rule?.title)
      //       : rule?.title;

      //     return {
      //       name, ruleId, isNegativeAmount, rows: table.data,
      //     };
      //   });

      // Group all selected rows by agentId across all tables
      const agentTotals = {};

      totalsTable.forEach(({
        name, isNegativeAmount, rows,
      }) => {
        rows.forEach((row) => {
          const rowAgentId = row.agentId;
          if (!rowAgentId) return;

          if (!agentTotals[rowAgentId]) {
            agentTotals[rowAgentId] = {};
          }

          const amount = Number(row.amountDue || row.AgentFee || 0);
          const value = isNegativeAmount ? -amount : amount;
          agentTotals[rowAgentId][name] = (agentTotals[rowAgentId][name] || 0) + value;
        });
      });

      // Fetch agent names for all unique agentIds
      const uniqueAgentIds = Object.keys(agentTotals);
      const agentNames = {};

      if (uniqueAgentIds.length > 0) {
        try {
          const { data: agents } = await fetchAgents({
            query: { _id: { $in: uniqueAgentIds } },
          });
          agents.forEach((agent) => {
            agentNames[agent._id] = agent.personalDetails?.fullName || agent._id;
          });
        } catch {
          uniqueAgentIds.forEach((aid) => { agentNames[aid] = aid; });
        }
      }

      const columnNames = [...new Set(totalsTable.map((item) => item.name))];
      const headersRow = ['תאריך', 'סוכן', ...columnNames, 'סכום לתשלום'];

      const summaryRows = uniqueAgentIds.map((aid) => {
        let rowTotal = 0;
        const agentRow = headersRow.reduce((acc, header) => {
          if (header === 'תאריך') {
            acc[header] = `${month[0]}/${year[0]}`;
          } else if (header === 'סוכן') {
            acc[header] = agentNames[aid] || aid;
          } else if (header === 'סכום לתשלום') {
            acc[header] = rowTotal.toFixed(2);
          } else {
            const val = agentTotals[aid][header];
            acc[header] = val ? Number(val.toFixed(2)) : '-';
            rowTotal += val || 0;
          }
          return acc;
        }, {});
        return agentRow;
      });

      // Add totals row
      const totalsRow = headersRow.reduce((acc, header) => {
        if (header === 'תאריך') {
          acc[header] = '';
        } else if (header === 'סוכן') {
          acc[header] = 'סה״כ';
        } else {
          const colTotal = summaryRows.reduce((sum, row) => {
            const val = Number(row[header]);
            return sum + (Number.isNaN(val) ? 0 : val);
          }, 0);
          acc[header] = colTotal.toFixed(2);
        }
        return acc;
      }, {});
      summaryRows.push(totalsRow);

      return {
        data: summaryRows,
        columns: headersRow.map((header) => ({ title: header, accessor: header, isVisible: true })),
        fileType: 'excel',
        titleArray: [`סיכום כולל - ${agentData?.personalDetails?.fullName || ''} - ${month[0]}/${year[0]}`],
        query: {},
      };
    }

    // Single agent - original logic
    const headersRow = totalsTable.map((item) => item.name);

    const rows = [
      totalsTable.reduce((acc, { name, isNegativeAmount, rows: tableRows }) => {
        let total = tableRows.reduce(
          (sum, row) => sum + Number(row.amountDue || row.AgentFee || 0),
          0,
        );
        if (isNegativeAmount) total = -total;
        acc[name] = total;
        return acc;
      }, {}),
    ];

    rows[0]['תאריך'] = `${month[0]}/${year[0]}`;
    rows[0]['סוכן'] = agentData?.personalDetails?.fullName || '';
    rows[0]['סכום לתשלום'] = totalAmount;
    headersRow.unshift('תאריך', 'סוכן');
    headersRow.push('סכום לתשלום');

    const summaryExport = {
      data: rows,
      columns: headersRow.map((header) => ({ title: header, accessor: header, isVisible: true })),
      fileType: 'excel',
      titleArray: [`סיכום כולל - ${agentData?.personalDetails?.fullName || ''} - ${month[0]}/${year[0]}`],
      query: {},
    };

    return summaryExport;
  };

  const handleSendEmail = async () => {
    const summaryFileData = await exportFileMutate(await buildSummeryTable());

    try {
      const createEmailFiles = await Promise.all(
        Object.entries(selectedRowsByTable)
          // eslint-disable-next-line no-unused-vars
          .filter(([_, rows]) => Array.isArray(rows) && rows.length > 0)
          .map(async ([tableKey, rows]) => {
            let columns;
            try {
              // Extract ruleId from tableKey (could be "ruleId" or "ruleId-variableId")
              const ruleId = tableKey.includes('-') ? tableKey.split('-')[0] : tableKey;
              // Find variable name if tableKey includes variableId
              let variableName = null;
              let visiblecolumnsTransforms = null;
              if (tableKey.includes('-')) {
                const variableId = tableKey.split('-')[1];
                const currentRule = rulesForMonthlyPayment?.find((rule) => rule._id === ruleId);
                const variable = currentRule?.variables?.find((v) => v._id === variableId);
                variableName = variable?.name || null;
                visiblecolumnsTransforms = variable?.visiblecolumnsTransforms;
                columns = variable?.visiblecolumns;
              }
              const generateExportFileObj = {
                ruleId,
                tableKey,
                variableName,
                exportRuleData,
                exportFileTitles,
                tableData,
                exportFileMutate,
                rows,
                columns,
                visiblecolumnsTransforms,
              };

              const { fileData, fileTitle } = await generateExportFile(generateExportFileObj);
              return { fileData, name: fileTitle };
            } catch (error) {
              return null;
            }
          }),
      );

      const validFiles = createEmailFiles.filter(Boolean);

      setFiles([{ fileData: summaryFileData, name: `סיכום כולל - ${agentData?.personalDetails?.fullName || ''} - ${month[0]}/${year[0]}.xlsx` }, ...validFiles]);
      openEmailModal();
    } catch {
      // TO DO SOMETHING
    }
  };

  const handleDownloadSummary = async () => {
    const summaryData = await buildSummeryTable();
    const fileData = await exportFileMutate(summaryData);
    downloadFile(fileData, 'סיכום כולל.xlsx');
  };

  const onEmailSent = () => {
    navigate(-1);
  };

  return (
    <PageTemplate
      withBackButton={true}
      title={t('pages.monthlyPayment.title', [agentData?.personalDetails?.fullName || ''])}
    >

      <MonthlyPaymentTables
        rulesForMonthlyPayment={rulesForMonthlyPayment}
        setRowsToPay={setRowsToPay}
        selectedRowsByTable={selectedRowsByTable}
        setSelectedRowsByTable={setSelectedRowsByTable}
        tableData={tableData}
        setTablesData={setTablesData}
        exportRuleData={exportRuleData}
        exportFileTitles={exportFileTitles}
        exportFileMutate={exportFileMutate}
        generateExportFile={generateExportFile}
      />
      <Footer
        totalAmount={totalAmount}
        label={t('pages.monthlyPayment.sum', ['{0}'])}
        handleSendEmail={handleSendEmail}
        isAllAgents={isAllAgents}
        handleDownloadSummary={handleDownloadSummary}
        isDataLoading={isDataLoading}
        hasSelectedRows={Object.values(selectedRowsByTable).some((rows) => rows.length > 0)}
      />

      <ModalEmailExport
        opened={opened}
        onClose={closeEmailModal}
        email={agentData?.personalDetails?.email}
        emailtemplateId={exportRuleData?.find((rule) => rule._id === id)?.emailTemplate || ''}
        rowsData={emailData}
        filePreview={files}
        onSend={updateRowsStatus}
        onEmailSent={onEmailSent}
        verifySendEmailProps={verifySendEmailProps}
      />
    </PageTemplate>
  );
};

export default ViewMonthlyPayment;
